'use client';

/**
 * Stateful orchestration for `ExperienceStep`, extracted into a custom hook
 * (conformance, code-size guard). Holds the experience list + inline-form
 * state, the load/search/rank effects, and all add/edit/delete handlers,
 * returning them for the thin `ExperienceStep` render shell. Pure field
 * derivation, payload building, and partitioning live alongside it in
 * `use-experience-manager.helpers.ts` / `.types.ts`.
 *
 * Called unconditionally at the top of the component, preserving the exact
 * hook-call order (BALLEE-52 invariant). The canonical add/edit/delete flow
 * (server-action calls, org-draft creation, RHF selection writes) stays
 * here — single edit surface, Critical Rule #8.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { toast } from '@kit/ui/sonner';

import {
  createExperienceAction,
  createExperienceOnBehalfAction,
  deleteExperienceAction,
  getExperiencesByDancerAction,
  updateExperienceAction,
  updateExperienceOnBehalfAction,
} from '../server/experience-actions';
import {
  findOrCreateUnverifiedOrganizationAction,
  setUnverifiedOrganizationTypeAction,
} from '../server/organization-actions';
import {
  getRanksByCategoryAction,
  getRanksByOrganizationAction,
} from '../server/rank-actions';
import type { ExperienceRow } from '../services/experience.service';
import {
  formTypeToOrgType,
  organizationSummaryToOrganization,
  orgTypeToFormType,
  type Experience,
  type FormType,
  type Organization,
  type Rank,
} from './experience-step-helpers';
import type { ExperienceStepProps } from './experience-step.types';
import { isLikelyAchievement } from './is-likely-achievement';
import type { OrganizationSummary } from './organization-types';
import {
  buildAwardPayload,
  buildExperiencePayload,
  deriveEditFormState,
  filterRanksByCategory,
  isAwardSubmissionValid,
  isExperienceSubmissionValid,
  partitionExperiences,
  rankCategoryForFormType,
  rowToExperience,
  shouldAutoOpenExperienceForm,
} from './use-experience-manager.helpers';
import type {
  ExperienceStepMode,
  ExperienceType,
} from './use-experience-manager.types';

/**
 * Sentinel thrown by `handleCreateOrg` when a typed name was routed to
 * `organization_manual` free text (achievement) instead of minting an org.
 * Throwing aborts `OrganizationPicker`'s create so no option is selected; the
 * routing side effects (manual name + type) were already applied.
 */
class AchievementRoutedError extends Error {
  constructor() {
    super('Routed to organization_manual as an achievement');
    this.name = 'AchievementRoutedError';
  }
}

export function useExperienceManager({
  professionalProfileId,
  mode = 'profile',
  formFieldName,
  filterTypes,
  onExperiencesChanged,
  actorRole = 'self',
  auditionId,
}: ExperienceStepProps) {
  const { t } = useTranslation('dancer');

  // Form context is consumed in select modes; RHF returns `null` when there's no parent FormProvider, and calling unconditionally is hooks-safe.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formContext = useFormContext<any>();

  // State
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [isPending, startTransition] = useTransition();
  const [isLoading, setIsLoading] = useState(!!professionalProfileId);

  // Form state
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [manualOrgName, setManualOrgName] = useState('');
  /**
   * Overrides the persisted `organization_manual_type` for the row being
   * saved. Set to `'achievement'` when the typed name tripped the
   * institution-vs-achievement classifier and the dancer chose to save it as
   * free text instead of minting an org (org faucet, phase 2). `null` for the
   * normal path (type is derived from the form type at save time).
   */
  const [manualOrgType, setManualOrgType] = useState<string | null>(null);
  const [formType, setFormType] = useState<FormType>('school');
  /** True when the inline form is in "add award" mode. Locks type=award. */
  const [isAwardMode, setIsAwardMode] = useState(false);
  /** True when the inline form panel is open. */
  const [showAddForm, setShowAddForm] = useState(false);
  const [availableRanks, setAvailableRanks] = useState<Rank[]>([]);
  const [selectedRank, setSelectedRank] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isCurrent, setIsCurrent] = useState(false);
  const [isWinner, setIsWinner] = useState(false);
  /** Award-specific: free-text placement (e.g. "1st Prize", "Gold Medal"). */
  const [placement, setPlacement] = useState('');
  /** Award-specific: 4-digit year input. Stored as `${year}-01-01`. */
  const [awardYear, setAwardYear] = useState('');
  /** Award-specific: optional category/variation, written to `title`. */
  const [awardTitle, setAwardTitle] = useState('');
  // Set when editing an existing experience; submit calls update instead of create. "Cancel" returns to create mode.
  const [editingId, setEditingId] = useState<string | null>(null);

  // True once the user dismisses the empty-state auto-open (Cancel) this mount, so the auto-open effect doesn't immediately re-fire.
  const userDismissedAutoOpenRef = useRef(false);

  // Institution-vs-achievement prompt (org faucet, phase 2). When a typed
  // "add new" name looks like an achievement, `handleCreateOrg` opens a
  // confirm dialog and awaits the dancer's decision via this resolver. The
  // name drives the dialog copy; `null` means no prompt is open.
  const [achievementPromptName, setAchievementPromptName] = useState<
    string | null
  >(null);
  const achievementResolverRef = useRef<
    ((decision: 'achievement' | 'organization') => void) | null
  >(null);

  // Notify parent on every experience-list change (apply wizard's localExperiences cache, used by the Review step).
  useEffect(() => {
    onExperiencesChanged?.(experiences);
  }, [experiences, onExperiencesChanged]);

  // Load existing experiences
  useEffect(() => {
    // No profile ID yet — skip; the step shows a fallback UI without breaking hook-call order.
    if (!professionalProfileId) {
      return;
    }

    let cancelled = false;
    const loadExperiences = async () => {
      setIsLoading(true);
      const result = await getExperiencesByDancerAction(professionalProfileId);
      if (cancelled) return;
      if (result.data) {
        setExperiences(result.data.map(rowToExperience));
      } else if (result.error) {
        toast.error(
          t('profileSetup.experience.loadError', 'Failed to load experiences'),
        );
      }
      setIsLoading(false);
    };

    void loadExperiences();
    return () => {
      cancelled = true;
    };
  }, [professionalProfileId, t]);

  // Load ranks when type or selected org changes: org-specific ranks first, falling back to category defaults. Awards have no ranks.
  useEffect(() => {
    if (!professionalProfileId) {
      return;
    }

    const loadRanks = async () => {
      // Awards and "other" have no rank list
      if (formType === 'other' || formType === 'award') {
        setAvailableRanks([]);
        return;
      }

      setSelectedRank('');
      const category = rankCategoryForFormType(formType);

      // Try org-specific ranks first when a known org is selected
      if (selectedOrg?.id) {
        const orgResult = await getRanksByOrganizationAction(selectedOrg.id);
        const filtered = filterRanksByCategory(orgResult.data ?? [], category);
        if (filtered.length > 0) {
          setAvailableRanks(filtered);
          return;
        }
      }

      // Fall back to category defaults
      const result = await getRanksByCategoryAction(category);
      if (result.data) {
        setAvailableRanks(result.data);
      } else if (result.error) {
        toast.error(
          t('profileSetup.experience.ranksLoadError', 'Failed to load ranks'),
        );
        setAvailableRanks([]);
      }
    };

    void loadRanks();
  }, [formType, selectedOrg, t, professionalProfileId]);

  // Reset form. `dismissedByUser: true` (Cancel button) suppresses the empty-state auto-open for the rest of this mount.
  const resetForm = useCallback((opts?: { dismissedByUser?: boolean }) => {
    setSelectedOrg(null);
    setManualOrgName('');
    setManualOrgType(null);
    setFormType('school');
    setIsAwardMode(false);
    setSelectedRank('');
    setStartDate('');
    setEndDate('');
    setIsCurrent(false);
    setIsWinner(false);
    setPlacement('');
    setAwardYear('');
    setAwardTitle('');
    setEditingId(null);
    setShowAddForm(false);
    if (opts?.dismissedByUser) {
      userDismissedAutoOpenRef.current = true;
    }
  }, []);

  // Open the inline form pre-configured for a given category
  const openExperienceForm = useCallback(() => {
    resetForm();
    setIsAwardMode(false);
    setFormType('school');
    setShowAddForm(true);
  }, [resetForm]);

  const openAwardForm = useCallback(() => {
    resetForm();
    setIsAwardMode(true);
    setFormType('award');
    setShowAddForm(true);
  }, [resetForm]);

  // Auto-open the experience form on empty state. See `shouldAutoOpenExperienceForm` (use-experience-manager.helpers) for the full rationale + skip conditions.
  useEffect(() => {
    if (
      !shouldAutoOpenExperienceForm({
        mode,
        hasProfile: !!professionalProfileId,
        isLoading,
        showAddForm,
        dismissedByUser: userDismissedAutoOpenRef.current,
        filterTypes,
        experiences,
      })
    ) {
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: synchronizing UI to a derived empty-state condition (load-completed && list-empty → form-open). Guarded by userDismissedAutoOpenRef + showAddForm so it fires at most once per mount.
    openExperienceForm();
  }, [
    mode,
    professionalProfileId,
    isLoading,
    showAddForm,
    experiences,
    filterTypes,
    openExperienceForm,
  ]);

  // Populate the form for editing. Field derivation lives in `deriveEditFormState` (use-experience-manager.helpers); this handler applies the derived state and owns the scroll side effect.
  const handleEditExperience = useCallback((exp: Experience) => {
    const state = deriveEditFormState(exp);

    setSelectedOrg(state.selectedOrg);
    setManualOrgName(state.manualOrgName);
    setFormType(state.formType);
    setIsAwardMode(state.isAwardMode);
    setSelectedRank(state.selectedRank);
    setStartDate(state.startDate);
    setEndDate(state.endDate);
    setIsCurrent(state.isCurrent);
    setIsWinner(state.isWinner);
    setPlacement(state.placement);
    setAwardYear(state.awardYear);
    setAwardTitle(state.awardTitle);

    setEditingId(exp.id);
    setShowAddForm(true);

    // Scroll the form card into view.
    if (typeof window !== 'undefined') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, []);

  // Add or update experience. Branches on `editingId` — null means create, set means update.
  const handleSubmitExperience = useCallback(async () => {
    if (!professionalProfileId) {
      return;
    }

    // Awards have a different validation path
    if (isAwardMode) {
      if (!isAwardSubmissionValid(selectedOrg, manualOrgName, placement)) {
        toast.error(
          t(
            'profileSetup.experience.awardRequired',
            'Competition name and placement are required',
          ),
        );
        return;
      }

      const payload = buildAwardPayload({
        selectedOrg,
        manualOrgName,
        placement,
        awardYear,
        awardTitle,
        manualOrgType,
      });

      startTransition(async () => {
        try {
          if (editingId) {
            const result =
              actorRole === 'admin'
                ? await updateExperienceOnBehalfAction(editingId, {
                    ...payload,
                    audition_id: auditionId!,
                  })
                : await updateExperienceAction(editingId, payload);
            if (result.data) {
              const updated = rowToExperience(result.data);
              setExperiences((prev) =>
                prev.map((e) => (e.id === editingId ? updated : e)),
              );
              resetForm();
              toast.success(
                t('profileSetup.experience.awardUpdated', 'Award updated'),
              );
            } else if (result.error) {
              toast.error(
                t(
                  'profileSetup.experience.awardUpdateError',
                  'Failed to update award. Please try again.',
                ),
              );
            }
          } else {
            const result =
              actorRole === 'admin'
                ? await createExperienceOnBehalfAction({
                    dancer_id: professionalProfileId,
                    audition_id: auditionId!,
                    ...payload,
                  })
                : await createExperienceAction({
                    dancer_id: professionalProfileId,
                    ...payload,
                  });
            if (result.data) {
              setExperiences((prev) => [
                rowToExperience(result.data!),
                ...prev,
              ]);
              resetForm();
              toast.success(
                t('profileSetup.experience.awardAdded', 'Award added'),
              );
            } else if (result.error) {
              toast.error(
                t(
                  'profileSetup.experience.awardAddError',
                  'Failed to add award. Please try again.',
                ),
              );
            }
          }
        } catch {
          toast.error(
            t(
              'profileSetup.experience.awardAddError',
              'Failed to add award. Please try again.',
            ),
          );
        }
      });
      return;
    }

    // Standard experience path (school / company / other)
    if (!isExperienceSubmissionValid(selectedOrg, manualOrgName)) {
      toast.error(
        t(
          'profileSetup.experience.orgRequired',
          'Organization name is required',
        ),
      );
      return;
    }

    // Snapshot at submit time so the post-success hooks see the user's intent even if the form state changes during the transition.
    const submittedSelectedOrg = selectedOrg;
    const submittedManualName = manualOrgName.trim();
    const submittedManualType = manualOrgType;
    const submittedFormType = formType;
    const orgTypeForBackfill = formTypeToOrgType(submittedFormType);

    startTransition(async () => {
      // Resolve organization_id. The find-or-create-unverified-org flow now
      // runs eagerly at selection time via `OrganizationPicker`'s `onCreate`
      // (see `handleCreateOrg`), so by submit time a typed-new-name org is
      // already a real row and `selectedOrg` is already populated — no
      // submit-time draft-creation fallback needed here anymore.
      const resolvedOrgId: string | null = submittedSelectedOrg?.id ?? null;

      // Defense-in-depth org-name fallback lives in `buildExperiencePayload`
      // (use-experience-manager.helpers) — see its JSDoc for why.
      const payload = buildExperiencePayload({
        resolvedOrgId,
        editingId,
        submittedSelectedOrg,
        submittedManualName,
        submittedFormType,
        selectedRank,
        isWinner,
        startDate,
        endDate,
        isCurrent,
        manualOrgType: submittedManualType,
      });

      // Freshest experience-id set, read via a no-op functional update so we
      // don't need `experiences` in this callback's deps. Lets the reconcile
      // below tell a row we just created apart from a pre-existing one.
      let priorIds = new Set<string>();
      setExperiences((prev) => {
        priorIds = new Set(prev.map((e) => e.id));
        return prev;
      });

      // Success side-effects for a created row. Idempotent on the list (a
      // reconciled row may already be present) so it's safe from either the
      // normal path or the reconcile path.
      const finalizeCreated = (row: ExperienceRow) => {
        const created = rowToExperience(row);
        setExperiences((prev) =>
          prev.some((e) => e.id === created.id) ? prev : [created, ...prev],
        );
        resetForm();
        // The draft+enrichment toast (`orgPendingEnrichment`) already fired at
        // org-creation time in `handleCreateOrg` (eager create at selection).
        toast.success(t('profileSetup.experience.added', 'Experience added'));

        // Step B: best-effort backfill of organization_type when the dancer
        // picked a known org with NULL type.
        if (submittedSelectedOrg && !submittedSelectedOrg.organization_type) {
          void setUnverifiedOrganizationTypeAction({
            organizationId: submittedSelectedOrg.id,
            type: orgTypeForBackfill,
          });
        }

        // select-affiliation mode: auto-select a freshly-created current
        // school/company so the dancer needn't scroll back to pick it.
        if (
          mode === 'select-affiliation' &&
          formFieldName &&
          formContext &&
          created.current === true &&
          (created.type === 'school' || created.type === 'company')
        ) {
          formContext.setValue(formFieldName, created.id, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      };

      // The create action can commit the row yet still surface a failure to the
      // client — a timed-out / aborted server-action round-trip on a slow
      // deploy lands the insert but the response never arrives, and the client
      // rejects. Re-fetch and look for the row we just tried to add (this org +
      // type, absent from the pre-submit snapshot) so a committed write isn't
      // reported as a failure and the dancer doesn't retry into a duplicate.
      const reconcileCreated = async (): Promise<ExperienceRow | null> => {
        try {
          const refetch = await getExperiencesByDancerAction(
            professionalProfileId,
          );
          if (!refetch.data) return null;
          return (
            refetch.data.find(
              (r) =>
                !priorIds.has(r.id) &&
                (r.organization_id ?? null) === resolvedOrgId &&
                r.type === submittedFormType,
            ) ?? null
          );
        } catch {
          return null;
        }
      };

      try {
        if (editingId) {
          const result =
            actorRole === 'admin'
              ? await updateExperienceOnBehalfAction(editingId, {
                  ...payload,
                  audition_id: auditionId!,
                })
              : await updateExperienceAction(editingId, payload);
          if (result.data) {
            const updated = rowToExperience(result.data);
            setExperiences((prev) =>
              prev.map((e) => (e.id === editingId ? updated : e)),
            );
            resetForm();
            toast.success(
              t('profileSetup.experience.updated', 'Experience updated'),
            );
          } else if (result.error) {
            toast.error(
              t(
                'profileSetup.experience.updateError',
                'Failed to update experience. Please try again.',
              ),
            );
          }
        } else {
          let created: ExperienceRow | null = null;
          try {
            const result =
              actorRole === 'admin'
                ? await createExperienceOnBehalfAction({
                    dancer_id: professionalProfileId,
                    audition_id: auditionId!,
                    ...payload,
                  })
                : await createExperienceAction({
                    dancer_id: professionalProfileId,
                    ...payload,
                  });
            created = result.data ?? null;
            // An explicit error can still coincide with a committed row on a
            // flaky round-trip — reconcile before believing the failure.
            if (!created && result.error) created = await reconcileCreated();
          } catch {
            // Client-side abort/timeout: the insert may have landed. Reconcile.
            created = await reconcileCreated();
          }
          if (created) {
            finalizeCreated(created);
          } else {
            toast.error(
              t(
                'profileSetup.experience.addError',
                'Failed to add experience. Please try again.',
              ),
            );
          }
        }
      } catch {
        // Reached only by the edit path above; the create path self-reconciles.
        toast.error(
          t(
            'profileSetup.experience.updateError',
            'Failed to update experience. Please try again.',
          ),
        );
      }
    });
  }, [
    editingId,
    selectedOrg,
    manualOrgName,
    manualOrgType,
    formType,
    isAwardMode,
    selectedRank,
    isWinner,
    placement,
    awardYear,
    awardTitle,
    startDate,
    endDate,
    isCurrent,
    professionalProfileId,
    resetForm,
    t,
    mode,
    formFieldName,
    formContext,
    actorRole,
    auditionId,
  ]);

  // Delete experience
  const handleDelete = useCallback(
    async (experienceId: string) => {
      startTransition(async () => {
        const result = await deleteExperienceAction(experienceId);
        if (result.success) {
          setExperiences((prev) =>
            prev.filter((exp) => exp.id !== experienceId),
          );
          toast.success(
            t('profileSetup.experience.deleted', 'Experience deleted'),
          );
        } else if (result.error) {
          toast.error(result.error);
        }
      });
    },
    [t],
  );

  // Select organization from `OrganizationPicker`'s `onSelect` (fires for
  // both a picked search result AND a freshly-created draft — the picker
  // auto-selects whatever `onCreate` returns).
  const handleSelectOrg = useCallback(
    (org: OrganizationSummary) => {
      const mapped = organizationSummaryToOrganization(org);
      setSelectedOrg(mapped);
      setManualOrgName('');
      // A concrete org was picked — drop any prior achievement free-text routing.
      setManualOrgType(null);

      // Auto-set form type from org.organization_type, unless award mode
      // (locked to 'award') or org has no type set (legacy — leave to dancer).
      if (!isAwardMode && mapped.organization_type) {
        setFormType(orgTypeToFormType(mapped.organization_type));
      }
    },
    [isAwardMode],
  );

  // Clear organization selection from `OrganizationPicker`'s `onDeselect`.
  const handleDeselectOrg = useCallback(() => {
    setSelectedOrg(null);
  }, []);

  // Find-or-create an unverified organization for a typed name with no
  // search match. Wired to `OrganizationPicker`'s `creatable` + `onCreate` —
  // the SAME draft-create path (`findOrCreateUnverifiedOrganizationAction`)
  // the standard (non-award) submit path previously called at submit time.
  // Runs eagerly at selection time now (the picker requires a real entity id
  // to select), for BOTH standard experiences and award-mode competition
  // names — award mode previously never created an org row for a typed
  // name (it only wrote `organization_manual`); this unifies both onto the
  // one dedup-aware draft-org path (BAL org-dedup, 2026-07-07).
  const handleCreateOrg = useCallback(
    async (name: string): Promise<OrganizationSummary> => {
      const trimmed = name.trim();

      // Institution-vs-achievement discipline (org faucet, phase 2): a typed
      // name that looks like an award / placement ("First Place at …",
      // "Finalist", a medal) should NOT mint an `organizations` row. Prompt
      // the dancer; the classifier is conservative (favours false-negatives),
      // and the dialog always offers an "it's an organization" override.
      if (isLikelyAchievement(trimmed)) {
        const decision = await new Promise<'achievement' | 'organization'>(
          (resolve) => {
            achievementResolverRef.current = resolve;
            setAchievementPromptName(trimmed);
          },
        );
        achievementResolverRef.current = null;
        setAchievementPromptName(null);

        if (decision === 'achievement') {
          // Free-text route: persist the typed name on the experience itself
          // (`organization_manual` + `organization_manual_type='achievement'`)
          // and create NO org row. Throwing aborts the picker's create so
          // nothing gets selected; `manualOrgName` keeps the Save button
          // enabled (see `hasOrgSelection`).
          setSelectedOrg(null);
          setManualOrgName(trimmed);
          setManualOrgType('achievement');
          throw new AchievementRoutedError();
        }
        // decision === 'organization': override — fall through and mint the org.
      }

      const orgTypeForBackfill = formTypeToOrgType(formType);
      const draft = await findOrCreateUnverifiedOrganizationAction({
        name: trimmed,
        type: orgTypeForBackfill,
      });

      if (!draft.success || !draft.id) {
        toast.error(
          t(
            'profileSetup.experience.orgCreateError',
            'Failed to create organization. Please try again.',
          ),
        );
        throw new Error(draft.error ?? 'Failed to create organization');
      }

      if (draft.created) {
        toast.success(
          t(
            'profileSetup.experience.orgPendingEnrichment',
            "Added {{name}}. We'll verify and enrich this organization shortly.",
            { name: trimmed },
          ),
        );
      }

      // Override path (or a name that never tripped the classifier): a real
      // org row now backs the selection — drop any prior achievement routing.
      setManualOrgType(null);

      return {
        id: draft.id,
        name: trimmed,
        organizationType: orgTypeForBackfill,
      };
    },
    [formType, t],
  );

  // Resolve the achievement prompt: "Save as achievement" routes to free text,
  // "It's an organization" overrides and mints the org.
  const confirmAchievement = useCallback(() => {
    achievementResolverRef.current?.('achievement');
  }, []);
  const overrideAsOrganization = useCallback(() => {
    achievementResolverRef.current?.('organization');
  }, []);

  // Save-button gate: an org must be selected/created, OR (legacy edit-mode
  // hydration only — no UI writes this anymore) a manual name carried over
  // from a pre-existing `organization_manual`-only experience row.
  const hasOrgSelection = !!selectedOrg || !!manualOrgName.trim();

  // Bucketing logic lives in `partitionExperiences` (use-experience-manager.helpers).
  const { experienceList, awardList, affiliationList, pastList } = useMemo(
    () => partitionExperiences(experiences, filterTypes),
    [experiences, filterTypes],
  );

  // Read currently-selected value(s) from the bound RHF field
  const selectedSingleId: string | null =
    mode === 'select-affiliation' && formContext && formFieldName
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        ((formContext.watch(formFieldName) as string | null) ?? null)
      : null;

  const selectedMultiIds: string[] =
    mode === 'select-experience' && formContext && formFieldName
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        ((formContext.watch(formFieldName) as string[] | undefined) ?? [])
      : [];

  // RHF setters (one-shot)
  const setSingle = useCallback(
    (id: string) => {
      if (!formContext || !formFieldName) return;
      formContext.setValue(formFieldName, id, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [formContext, formFieldName],
  );

  const togglePast = useCallback(
    (id: string, checked: boolean) => {
      if (!formContext || !formFieldName) return;
      const current = selectedMultiIds;
      const next = checked
        ? Array.from(new Set([...current, id]))
        : current.filter((x) => x !== id);
      formContext.setValue(formFieldName, next, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [formContext, formFieldName, selectedMultiIds],
  );

  return {
    t,
    isPending,
    isLoading,
    // Inline form state
    selectedOrg,
    hasOrgSelection,
    formType,
    setFormType,
    isAwardMode,
    showAddForm,
    availableRanks,
    selectedRank,
    setSelectedRank,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    isCurrent,
    setIsCurrent,
    isWinner,
    setIsWinner,
    placement,
    setPlacement,
    awardYear,
    setAwardYear,
    awardTitle,
    setAwardTitle,
    editingId,
    // Handlers
    resetForm,
    openExperienceForm,
    openAwardForm,
    handleEditExperience,
    handleSubmitExperience,
    handleDelete,
    onOrgSelect: handleSelectOrg,
    onOrgDeselect: handleDeselectOrg,
    onOrgCreate: handleCreateOrg,
    // Institution-vs-achievement prompt (org faucet, phase 2)
    achievementPromptName,
    confirmAchievement,
    overrideAsOrganization,
    // Partitioned lists
    experienceList,
    awardList,
    affiliationList,
    pastList,
    // RHF selection (select modes)
    selectedSingleId,
    selectedMultiIds,
    setSingle,
    togglePast,
  };
}

export type ExperienceManager = ReturnType<typeof useExperienceManager>;

export type { ExperienceType, ExperienceStepMode };
